import Vue from "vue";
import Vuex from "vuex";

import {cloneDeep} from 'lodash-es';

export default class {
  constructor() {
    Vue.use(Vuex);
    const state = {
      loaded: false,
      selectedPageIndex: 0,
      submittedSearchTerm: "",
      selectedSort: "relevancy",
      searchQuery: {
        searchTerm: "",
        facets: [],
        filters: [],
        alphaFilter: "",
        currentPage: 0,
        pageSize: 20,
        searchIndex: "",
        filterGroups: "",
        showContentType: false,
        preselectedFilters: [],
        orderBy: []
      },
      results: {
        queryUsed: {},
        itemResults: [],
        filters: [],
        totalResultCount: 0,
        currentPage: 0,
        pageSize: 0,
        totalPages: 0
      },
      settings: {
        searchUrl: "",
        headerText: "Search Results",
        searchPlaceholder: "Search term",
        searchButtonText: "Search",
        filterText: "Refine Results",
        filterButtonText: "Apply Filters"
      }
    };

    const getters = {
      loaded: state => state.loaded,
      selectedPageIndex: state => state.selectedPageIndex,
      searchQuery: state => state.searchQuery,
      results: state => state.results,
      settings: state => state.settings,
      totalResultCount: state => state.results.totalResultCount,
      submittedSearchTerm: state => state.submittedSearchTerm,
      selectedSort: state => state.selectedSort,
      currentResultsStart (state) {
        if (state.results.currentPage == 0) {
          return 1;
        } else {
          return state.results.currentPage * state.results.pageSize + 1;
        }
      },
      currentResultsEnd (state) {
        if (state.results.currentPage == 0) {
          return Math.min(state.results.totalResultCount, state.results.pageSize);
        } else {
          const max = (state.results.currentPage + 1) * state.results.pageSize;
          return Math.min(max, state.results.totalResultCount);
        }
      }
    };

    const mutations = {
      setLoaded(state, loaded) {
        state.loaded = loaded;
      },
      setInlineSettings(state, inlineSettings) {
        state.settings = { ...state.settings, ...inlineSettings };
      },
      setQuery(state, searchTerm) {
        state.searchQuery.searchTerm = searchTerm;
      },
      setAlphaFilter(state, letter) {
        state.searchQuery.alphaFilter = letter;
      },
      setPreselectedFilters(state, preselectedFilters) {
        state.searchQuery.preselectedFilters = preselectedFilters;
      },
      setPageIndex(state, data) {
        state.searchQuery.currentPage = parseInt(data);
      },
      // setFilters(state, data) {
      //   state.searchQuery.filters = data;
      // },
      setFilterItem(state, {groupID, id, value}) {
        let filters = cloneDeep(state.searchQuery.filters);
        let group = filters.find(group => group.id === groupID);
        let filter = group.filters.find(filter => filter.id === id);
        filter.selected = value;
        state.searchQuery.filters = [...filters];
      },
      setShowContentType(state, data) {
        state.searchQuery.showContentType = Boolean(data);
      },
      setResults(state, results) {
				let newResults = cloneDeep(results);
        state.searchQuery.filters = cloneDeep(newResults.filters);
        state.results = newResults;
      },
      setPageSize(state, size) {
        state.searchQuery.pageSize = parseInt(size);
      },
      setSearchIndex(state, index) {
        state.searchQuery.searchIndex = index;
      },
      setSettings(state, settings) {
        state.settings = settings;
      },
      setOrderBy(state, order) {
        state.searchQuery.orderBy = [order];
      },
      setSubmittedSearchTerm(state) {
        state.submittedSearchTerm = state.searchQuery.searchTerm;
      },
      setSelectedSort(state, sort) {
        state.selectedSort = sort;
      }
    };

    const actions = {
      setInitialValues({ commit, state }, route) {
        commit("setSearchIndex", state.settings.searchIndex);
        commit("setPageSize", state.settings.pageSize);
        commit("setShowContentType", state.settings.showContentType);

        // Check URL to see if any parameters are set
        if (route.query.searchtext) {
          commit("setQuery", route.query.searchtext);
        }

        if (route.query.p) {
          commit("setPageIndex", route.query.p);
        }

        if (route.query.a) {
          commit("setAlphaFilter", route.query.a);
        }

        if (route.query.c) {
          commit("setPreselectedFilters", route.query.c.split(','));
        }
      },
      executeSearch({ commit, state }) {
        if(state.searchQuery.searchTerm.trim() === "") {
          commit("setSubmittedSearchTerm");
          commit("setLoaded", true);
          return;
        }

        const url = state.settings.searchUrl;
        const body = JSON.stringify(state.searchQuery);

        commit("setSubmittedSearchTerm");
        commit("setLoaded", false);

        return new Promise(async resolve => {
          const response = await fetch(url, {
            method: "POST",
            mode: "cors",
            cache: "no-cache",
            credentials: "same-origin",
            headers: {
              "Content-Type": "application/json"
            },
            redirect: "follow",
            referrerPolicy: "no-referrer",
            body
          });
          const results = await response.json();
          commit("setResults", results);

          if (state.selectedPageIndex > results.totalPages - 1) {
            commit("setPageIndex", 0);
          }

          commit("setLoaded", true);
          resolve(true);
        });
      },
    };

    this.store = new Vuex.Store({
      state,
      getters,
      mutations,
      actions
    });
  }
}
